"use client";

import React from "react";
import { ArrowRight, ArrowUpRight, CircleCheck as CheckCircle2, Cpu, Globe as Globe2, Target } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  title: "B2B SEO & Lead Generation",
  to: "/B2BSeoLeadGenerationPage",
  badge: "Revenue & Pipeline",
  description: "Target decision-makers actively seeking your software, industrial capabilities, or specialist advisory.",
  deliverables: ["High-intent commercial search mapping", "Decision-maker content architectures", "Bottom-funnel conversion path optimisation"],
  highlight: "Qualified buyer search intent"
}, {
  title: "Technical SEO",
  to: "/TechnicalSeoPage",
  badge: "Architecture & Speed",
  description: "Eliminate crawl traps, resolve rendering bottlenecks, and construct robust structured data foundations.",
  deliverables: ["In-depth crawl and indexation diagnostics", "Core Web Vitals and performance tuning", "Schema markup and canonical structure"],
  highlight: "Clean indexing and crawl efficiency"
}, {
  title: "National SEO",
  to: "/NationalSeoPage",
  badge: "Market Visibility",
  description: "Establish non-branded category authority across the UK for competitive high-volume queries.",
  deliverables: ["Topical authority gap analysis", "Scalable information architecture", "Competitive organic positioning strategy"],
  highlight: "Sustained UK-wide ranking power"
}];
export default function Directory() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="coreservicesdirectory" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="h-2 w-2 rounded-full bg-accent" />
            <span className="text-xs uppercase tracking-wider font-semibold text-primary">
              Core Strategic Services
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold tracking-tight text-foreground leading-tight">
            Direct SEO advisory for sustainable search growth
          </h2>

          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every engagement is planned, audited, and executed directly by me. No junior handlers,
            no outsourced layers, and no cookie-cutter agency retainers.
          </p>
        </div>

        {/* Directory Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => <motion.div key={service.title} variants={itemVariants} data-index={i}>
              <Card className="h-full border border-border bg-card/50 hover:bg-card hover:border-primary/40 transition-[background-color,border-color,box-shadow] duration-300 flex flex-col justify-between shadow-xs hover:shadow-md">
                <div>
                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="secondary" className="bg-secondary text-secondary-foreground border-transparent text-xs font-medium px-2.5 py-1">
                        {service.badge}
                      </Badge>
                      <div className="text-primary/70">
                        {i === 0 && <Target className="size-5" />}
                        {i === 1 && <Cpu className="size-5" />}
                        {i === 2 && <Globe2 className="size-5" />}
                      </div>
                    </div>

                    <CardTitle className="text-xl sm:text-2xl font-bold font-default text-foreground pt-1">
                      <Link to={service.to} className="hover:text-primary transition-colors inline-flex items-center gap-1.5 group">
                        <span>{service.title}</span>
                        <ArrowUpRight className="size-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </Link>
                    </CardTitle>

                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2">
                    <div className="text-xs uppercase font-semibold text-muted-foreground tracking-wider mb-3">
                      Focus Deliverables
                    </div>
                    <ul className="space-y-2.5 text-sm">
                      {service.deliverables.map((item, deliverableIdx) => <li key={deliverableIdx} data-index={deliverableIdx} className="flex items-start gap-2.5 text-foreground/90">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </div>

                <CardFooter className="pt-6 border-t border-border mt-6">
                  <Button asChild variant="outline" className="w-full justify-between group">
                    <Link to={service.to}>
                      <span className="text-sm font-medium">Explore {service.title}</span>
                      <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Action Banner */}
        <div className="mt-14 md:mt-20 p-8 sm:p-10 rounded-2xl bg-secondary/50 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold text-accent uppercase tracking-wider">
              One-to-one Partnership
            </span>
            <h3 className="text-xl sm:text-2xl font-default font-bold text-foreground mt-1">
              Need a clear diagnostic before committing to a scope?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Speak directly with me to review your organic search bottlenecks and evaluate strategic
              opportunities.
            </p>
          </div>

          <div className="shrink-0 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
              <Link to="/ContactPage">Book a Free SEO Consultation</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}